import type { NavigateFunction } from 'react-router-dom';
import { configs } from '@constants/configs/configs';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';

const MS_PREFIX = 'ms:';

export type UserRole = (typeof ALL_USER_ROLES)[keyof typeof ALL_USER_ROLES];

export const isAbsoluteLandingPage = (url?: string | null): boolean =>
    !!url && /^https?:\/\//i.test(url);

export const isMsPrefixedLandingPage = (url?: string | null): boolean =>
    !!url &&
    url.length >= MS_PREFIX.length &&
    url.slice(0, MS_PREFIX.length).toLowerCase() === MS_PREFIX;

/**
 * Resolves a landing page value from /environment for SPA navigation.
 * - Full/absolute URL (http/https): navigate directly, no prefix.
 * - ms: URL (peer/MS-UI): strip ms: and prepend app server/origin; do not prefix /hix/.
 * - Partial URL (internal HIX): prefix with the HIX route base.
 * - Null/undefined/empty: falls back to the home base alone.
 */
export const resolveLandingPageNavigation = (
    landingPage: string | null | undefined,
    homeBase: string,
    appServer?: string,
) => {
    if (isAbsoluteLandingPage(landingPage)) {
        return { external: true, href: landingPage as string };
    }

    if (isMsPrefixedLandingPage(landingPage)) {
        const origin = (appServer ?? window.location.origin).replace(/\/$/, '');
        return {
            external: true,
            href: `${origin}${landingPage!.substring(MS_PREFIX.length)}`,
        };
    }

    const normalizedHomeBase = homeBase.endsWith('/') ? homeBase : `${homeBase}/`;
    return {
        external: false,
        href: `${normalizedHomeBase}${(landingPage ?? '').replace(/^\//, '')}`,
    };
};

export const navigateToLandingPage = (
    landingPage: string | null | undefined,
    navigate: NavigateFunction,
    homeBase: string,
    appServer?: string,
): void => {
    const { external, href } = resolveLandingPageNavigation(landingPage, homeBase, appServer);
    if (external) {
        window.location.href = href;
    } else {
        navigate(href);
    }
};

/**
 * Role-aware landing navigation for preferences (and similar) flows.
 * - INDIVIDUAL: resolve environment landingPage (relative / http(s) / ms:).
 * - All other roles: INDPORTAL (legacy member portal path).
 */
export const navigateByRole = (
    role: UserRole | null | undefined,
    landingPage: string | null | undefined,
    navigate: NavigateFunction,
): void => {
    if (role === ALL_USER_ROLES.INDIVIDUAL) {
        navigateToLandingPage(landingPage, navigate, configs.routes.HOME);
    } else {
        navigate(configs.routes.INDPORTAL);
    }
};
